import { createContext, useContext, useMemo } from 'react';
import { isPermissionDowngrade, calculateImpactedUsersCount } from '../utils/installPermissions';
import { useInstallPermissions } from './InstallPermissionsContext';
import { useFetchInstalledUsers } from '../hooks/queries/useFetchInstalledUsers';
import { useFetchUsersWithInstallAccess } from '../hooks/queries/useFetchUsersWithInstallAccess';
import { jsx as _jsx } from "react/jsx-runtime";
const defaultContextValue = {
  showAlert: false,
  impactedUserCount: 0,
  newInstallCount: undefined
};
const InstallPermissionsDowngradeContext = /*#__PURE__*/createContext(defaultContextValue);
export function InstallPermissionsDowngradeProvider({
  children
}) {
  const {
    installPermissionAccessState,
    initialInstallPermissionAccessState,
    getSelectedEntityTypeAndIds
  } = useInstallPermissions();
  const {
    data: installedUsersData
  } = useFetchInstalledUsers();
  const {
    superAdmins,
    integrationsManagementWriteUsers
  } = useFetchUsersWithInstallAccess();
  const selectedUserIds = useMemo(() => getSelectedEntityTypeAndIds(), [getSelectedEntityTypeAndIds]);
  const impactedUserCount = useMemo(() => {
    var _installedUsersData$i;
    const installedUsers = (_installedUsersData$i = installedUsersData === null || installedUsersData === void 0 ? void 0 : installedUsersData.infoForInstalledUsers) !== null && _installedUsersData$i !== void 0 ? _installedUsersData$i : [];
    if (installedUsers.length === 0) {
      return 0;
    }
    const isDowngrade = isPermissionDowngrade(initialInstallPermissionAccessState, installPermissionAccessState);
    if (!isDowngrade) {
      return 0;
    }
    return calculateImpactedUsersCount({
      currentState: initialInstallPermissionAccessState,
      newState: installPermissionAccessState,
      installedUsers,
      selectedUserIds,
      superAdmins,
      authorizedInstallers: integrationsManagementWriteUsers
    });
  }, [installedUsersData, initialInstallPermissionAccessState, installPermissionAccessState, selectedUserIds, superAdmins, integrationsManagementWriteUsers]);
  const showAlert = impactedUserCount > 0;
  const newInstallCount = useMemo(() => {
    var _installedUsersData$i2, _installedUsersData$i3;
    if (!showAlert) {
      return undefined;
    }
    const totalInstalledCount = (_installedUsersData$i2 = installedUsersData === null || installedUsersData === void 0 || (_installedUsersData$i3 = installedUsersData.infoForInstalledUsers) === null || _installedUsersData$i3 === void 0 ? void 0 : _installedUsersData$i3.length) !== null && _installedUsersData$i2 !== void 0 ? _installedUsersData$i2 : 0;
    return totalInstalledCount - impactedUserCount;
  }, [showAlert, installedUsersData, impactedUserCount]);
  const value = useMemo(() => ({
    showAlert,
    impactedUserCount,
    newInstallCount
  }), [showAlert, impactedUserCount, newInstallCount]);
  return /*#__PURE__*/_jsx(InstallPermissionsDowngradeContext.Provider, {
    value: value,
    children: children
  });
}
export function useInstallPermissionsDowngrade() {
  return useContext(InstallPermissionsDowngradeContext);
}